import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Waveform } from "@/components/common";

export default function Auth({ mode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") { await register(email, password, name); toast.success("Welcome to JixxeoX!"); navigate("/"); }
      else if (mode === "login") { await login(email, password); toast.success("Signed in"); navigate("/"); }
      else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: sp.get("token"), password });
        toast.success("Password updated. Please sign in."); navigate("/login");
      }
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  const forgot = async () => {
    if (!email) return toast.error("Enter your email first");
    try { await api.post("/auth/forgot-password", { email }); toast.success("If the email exists, a reset link was sent (check server logs in dev)."); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090b]">
      <div className="hidden lg:flex relative flex-col justify-between p-12 jx-radial-bloom overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <img src="/icon-192.png" className="w-10 h-10 rounded-xl" alt="" />
          <span className="text-2xl font-black font-display jx-text-gradient">JixxeoX</span>
        </Link>
        <div className="relative z-10">
          <Waveform bars={18} className="h-12 mb-6" />
          <h1 className="text-4xl font-black font-display leading-tight">Your Voice.<br />Your Sound.<br /><span className="jx-text-gradient">Your World.</span></h1>
          <p className="text-slate-400 mt-4 max-w-sm">Create AI music, stream what the community builds, and earn from every listen.</p>
        </div>
        <p className="text-xs text-slate-600 relative z-10">© 2026 JixxeoX</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-4">
            <img src="/icon-192.png" className="w-9 h-9 rounded-xl" alt="" />
            <span className="text-xl font-black font-display jx-text-gradient">JixxeoX</span>
          </div>
          <h2 className="text-2xl font-bold font-display">
            {mode === "register" ? "Create your account" : mode === "reset" ? "Set a new password" : "Welcome back"}
          </h2>

          {mode === "register" && (
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" required data-testid="auth-name"
              className="w-full bg-[#121217] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50" />
          )}
          {mode !== "reset" && (
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" required data-testid="auth-email"
              className="w-full bg-[#121217] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50" />
          )}
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" required data-testid="auth-password"
            className="w-full bg-[#121217] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50" />

          {mode === "login" && <button type="button" onClick={forgot} className="text-xs text-purple-400">Forgot password?</button>}

          <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full jx-cta text-white font-semibold py-3 rounded-xl disabled:opacity-50">
            {loading ? "Please wait…" : mode === "register" ? "Create Account" : mode === "reset" ? "Update Password" : "Sign In"}
          </button>

          {mode === "login" && <p className="text-sm text-slate-400 text-center">New here? <Link to="/register" className="text-purple-400 font-semibold">Create a free account</Link></p>}
          {mode === "register" && <p className="text-sm text-slate-400 text-center">Already a member? <Link to="/login" className="text-purple-400 font-semibold">Sign in</Link></p>}
          <Link to="/" className="block text-center text-xs text-slate-500">← Back to music</Link>
        </form>
      </div>
    </div>
  );
}
